import { Injectable } from "@angular/core"
import { AngularFirestore, AngularFirestoreCollection, AngularFirestoreDocument, QueryFn } from "@angular/fire/firestore"
import { CompanyUser, TestCase } from "@tr/models"
import { Observable } from "rxjs"
import { map } from "rxjs/operators"

@Injectable({
  providedIn: "root",
})
export class FirebaseService {
  constructor(
    private afs: AngularFirestore,
  ) { }

  public collection<T = any>(collection: string): AngularFirestoreCollection<T> {
    return this.afs.collection<T>(collection)
  }

  public doc<T = any>(documentPath: string): AngularFirestoreDocument<T> {
    return this.afs.doc<T>(documentPath)
  }

  public async getUsersByCompany(companyId: string): Promise<CompanyUser[]> {
    return this.getCollectionData$<CompanyUser>(
      this.companyUsersCollection(companyId),
    ).toPromise()
  }

  public getUsersByCompany$(companyId: string): Observable<CompanyUser[]> {
    return this.getCollectionData$<CompanyUser>(
      this.companyUsersCollection(companyId),
    )
  }

  public companyDoc<T = any>(companyId): AngularFirestoreDocument<T> {
    return this.afs
      .collection("companies")
      .doc(companyId)
  }

  public companyProjectDoc<T = any>(companyId, projectId): AngularFirestoreDocument<T> {
    return this.afs
      .collection("companies")
      .doc(companyId)
      .collection("projects")
      .doc(projectId)
  }

  public getTestCases<T = any>(company: string, project: string): AngularFirestoreCollection<T> {
    return this.afs
      .collection("companies")
      .doc(company)
      .collection("projects")
      .doc(project)
      .collection("tests")
  }

  public async getTestCase(company: string, project: string, id: string): Promise<any> {
    return this.getDocument<any>(
      this.getTestCases(company, project)
        .doc(id),
    )
  }

  public usersCollection<T = any>(): AngularFirestoreCollection {
    return this.afs
      .collection("users")
  }

  public usersDoc<T = any>(uid: string): AngularFirestoreDocument {
    return this.usersCollection<T>().doc(uid)
  }

  public companyUsersCollection<T = any>(companyId, ref?: QueryFn): AngularFirestoreCollection {
    return this.companyDoc<T>(companyId).collection("users", ref)
  }

  public companyProjectTestPlans<T = any>(companyId, projectId): AngularFirestoreCollection {
    return this.companyProjectDoc<T>(companyId, projectId).collection("Test Plans")
  }

  // General Commands
  public getDocument$<T>(documentRef: AngularFirestoreDocument): Observable<T> {
    return documentRef
      .get()
      .pipe(
        map(snapshot => {
          return snapshot.exists
            ? {
              ...snapshot.data() as any,
              id: snapshot.id,
            } as T
            : null
        }),
      )
  }

  public async getDocument<T>(documentRef: AngularFirestoreDocument, withId = true): Promise<T> {
    const userDoc = await documentRef.get().toPromise()
    return userDoc.exists
      ? {
        ...userDoc.data() as any,
        id: withId ? userDoc.id : undefined,
      } as T
      : null
  }

  public getCollectionData$<T>(collectionRef: AngularFirestoreCollection): Observable<T[]> {
    return collectionRef
      .get()
      .pipe(
        map(snapshot => {
          return snapshot.empty
            ? []
            : snapshot.docs.map(doc => {
              return {
                ...doc.data() as any,
                id: doc.id,
              } as T
            })
        }),
      )
  }

  public async getCollectionData<T>(documentRef: AngularFirestoreCollection, withId = true): Promise<T[]> {
    const userDoc = await documentRef.get().toPromise()
    return getCollectionData(userDoc, withId)
  }
}

export function getCollectionData<T = any>(userDoc, withId = true): T[] {
  return userDoc.empty
    ? []
    : userDoc.docs.map(doc => {
      return {
        ...doc.data() as any,
        id: withId ? doc.id : undefined,
      } as T
    })
}
